/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
/* eslint-disable no-underscore-dangle */
import { useLazyQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import chunk from 'lodash/chunk';
import { GetFactsQuery, RequestQuestion } from '../generated/graphql';
import AppConstants from '../utils/AppConstants';
import AppUtil from '../utils/AppUtil';
import FormattingUtil from '../utils/FormattingUtil';

interface IRequestQuestionChoicesProps {
  question: RequestQuestion;
  knowledgeId: string | null;
}

interface IRequestQuestionChoicesResponse {
  choices: string[];
  loading: boolean;
  choicesRawValuesMap: Record<number, any>; // Record<index of choice, choice raw value>
}

const CHUNK_SIZE = 50;

function useRequestQuestionChoices(
  props: IRequestQuestionChoicesProps
): IRequestQuestionChoicesResponse {
  const { question, knowledgeId } = props;

  const { id } = question;

  const [choices, setChoices] = useState<string[]>([]);
  const [factIds, setFactIds] = useState<string[]>([]);

  const [choicesRawValuesMap, setChoicesRawValuesMap] = useState({});

  // Loading state is different from query loading because, computation takes time as well
  const [loading, setLoading] = useState(false);

  // TODO: Use useFact
  const [getFacts] = useLazyQuery<GetFactsQuery>(
    AppConstants.APIS.GET_ENTITY.facts()
  );

  // If factIds.length is a lot, then getFacts may give a 503, hence chunking and parallel fetching
  const getAllFacts = async () => {
    const chunks = chunk(factIds, CHUNK_SIZE);
    try {
      const response = await Promise.all(
        chunks.map((chunk) =>
          getFacts({
            variables: {
              factIds: Array.from(chunk),
              knowledgeId
            }
          })
        )
      );
      if (response[0].data?.getFacts) {
        return response.map((res) => res.data?.getFacts).flat();
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch facts:', error);
      return [];
    }
  };

  useEffect(() => {
    const questionChoices = (question as any).choices || [];
    if (questionChoices?.[0]) {
      const { choices: initialChoices } = JSON.parse(questionChoices[0]) || {};

      if (
        initialChoices.length === 2 &&
        initialChoices.includes(true) &&
        initialChoices.includes(false)
      ) {
        setChoices(['Yes', 'No']);
      } else {
        const factIdsFromChoices = initialChoices.reduce(
          (acc: string[], cur: any) => {
            if (typeof cur === 'object' && '__concept__' in cur) {
              acc.push(cur.__concept__.id);
            }
            return acc;
          },
          []
        );
        setFactIds(factIdsFromChoices);

        const newChoices = initialChoices.map((choice: any) => {
          if (typeof choice !== 'object') {
            return choice;
          }
          if ('__concept__' in choice) {
            return choice.__concept__.id;
          }
          if ('value' in choice) {
            return choice.value;
          }
          return choice;
        });
        setChoices(newChoices);
      }
    }
  }, [id]);

  useEffect(() => {
    if (factIds.length > 0 && knowledgeId) {
      setLoading(true);
      getAllFacts()
        .then((allFacts) => {
          if (allFacts.length > 0) {
            if (allFacts) {
              const facts = allFacts;

              const parsedFacts = facts.map((fact) => {
                const resp = {
                  id: fact?.dereferencedId || fact?.id,
                  value: '',
                  rawValue: null
                };
                const parsedValue = FormattingUtil.parseBrainValue(
                  fact?.value || ''
                );

                if (parsedValue) {
                  const type = typeof parsedValue;
                  resp.rawValue = parsedValue;

                  if (
                    ['string', 'number', 'boolean'].includes(type) ||
                    !parsedValue
                  ) {
                    resp.value = parsedValue;
                  } else if (Array.isArray(parsedValue)) {
                    resp.value = parsedValue[0];
                  } else if (type === 'object') {
                    const name = AppUtil.getObjectName(parsedValue);
                    const id = AppUtil.getObjectId(parsedValue);
                    const updatedValue =
                      name || id || AppUtil.getFirstKeyFromObject(parsedValue);
                    resp.value = `${updatedValue}`;
                  }
                } else {
                  resp.value = fact?.names?.[0] || '';
                }
                return resp;
              });

              const updatedChoices = choices.map((choice, index) => {
                const resp = {
                  value: '',
                  index,
                  rawValue: null
                };

                const choiceInFacts = parsedFacts.find(
                  (fact) => fact.id === choice
                );
                if (choiceInFacts) {
                  resp.value = choiceInFacts.value;
                  resp.rawValue = choiceInFacts.rawValue;
                }

                return resp;
              });

              const updatedChoicesRawValuesMap = updatedChoices.reduce<any>(
                (acc, cur) => {
                  acc[cur.index] = cur.rawValue;
                  return acc;
                },
                {}
              );

              setChoicesRawValuesMap(updatedChoicesRawValuesMap);
              setChoices(updatedChoices.map((choice) => choice.value));
            }
          }
        })
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factIds, knowledgeId]);

  return {
    choices,
    loading,
    choicesRawValuesMap
  };
}

export default useRequestQuestionChoices;
